interface ProjectGraphicProps {
  src: string
  alt: string
  className?: string
  loading?: 'eager' | 'lazy'
  animated?: boolean
}

function TwilabethGraphic({ alt, className }: Pick<ProjectGraphicProps, 'alt' | 'className'>) {
  return (
    <div className={`project-graphic twilabeth-graphic ${className}`.trim()}>
      <svg viewBox="0 0 1000 620" role="img" aria-label={alt} preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="twilabeth-node" cx="42%" cy="34%" r="72%">
            <stop offset="0" stopColor="#17232b" />
            <stop offset="0.58" stopColor="#070b0e" />
            <stop offset="1" stopColor="#000" />
          </radialGradient>
          <radialGradient id="twilabeth-core" cx="46%" cy="40%" r="65%">
            <stop offset="0" stopColor="#182a34" />
            <stop offset="0.55" stopColor="#080d10" />
            <stop offset="1" stopColor="#000" />
          </radialGradient>
          <filter id="twilabeth-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        <g className="twilabeth-grid" aria-hidden="true">
          <path d="M0 104H1000M0 310H1000M0 516H1000" />
          <path d="M167 0V620M500 0V620M833 0V620" />
        </g>

        <g className="twilabeth-connections" aria-hidden="true">
          <path pathLength="1" d="M430 265L301 171" />
          <path pathLength="1" d="M570 265L699 171" />
          <path pathLength="1" d="M425 363L279 438" />
          <path pathLength="1" d="M575 363L721 430" />
          <path pathLength="1" d="M500 401V477" />
        </g>

        <g className="twilabeth-signals" aria-hidden="true">
          <circle cx="355" cy="210" r="5" />
          <circle cx="645" cy="210" r="5" />
          <circle cx="350" cy="400" r="5" />
          <circle cx="650" cy="398" r="5" />
          <circle cx="500" cy="440" r="5" />
        </g>

        <g className="twilabeth-node twilabeth-node-phone" transform="translate(245 130)">
          <circle r="82" className="twilabeth-node-halo" />
          <circle r="68" className="twilabeth-node-surface" />
          <path className="twilabeth-icon" d="M-28-31c5-5 14-5 18 1l12 17c3 5 2 11-2 15l-9 8c9 18 22 31 40 40l8-9c4-4 10-5 15-2l17 12c6 4 6 13 1 18l-10 10c-7 7-18 9-27 5-35-15-63-43-78-78-4-9-2-20 5-27z" />
          <path className="twilabeth-accent-stroke" d="M24-32c16 4 28 16 32 32M21-13c8 2 14 8 16 16" />
        </g>

        <g className="twilabeth-node twilabeth-node-message" transform="translate(755 130)">
          <circle r="82" className="twilabeth-node-halo" />
          <circle r="68" className="twilabeth-node-surface" />
          <path className="twilabeth-icon-stroke" d="M-40-30h80c9 0 16 7 16 16v35c0 9-7 16-16 16H3l-28 23 5-23h-20c-9 0-16-7-16-16v-35c0-9 7-16 16-16z" />
          <circle className="twilabeth-dot" cx="-18" cy="4" r="5" /><circle className="twilabeth-dot" cx="0" cy="4" r="5" /><circle className="twilabeth-dot" cx="18" cy="4" r="5" />
        </g>

        <g className="twilabeth-node twilabeth-node-browser" transform="translate(220 470)">
          <circle r="82" className="twilabeth-node-halo" />
          <circle r="68" className="twilabeth-node-surface" />
          <rect className="twilabeth-icon-stroke" x="-43" y="-35" width="86" height="70" rx="7" />
          <path className="twilabeth-icon-stroke" d="M-43-17h86M-28 1h22v20h-22zM7 1h22v20H7z" />
          <circle className="twilabeth-dot" cx="-28" cy="-26" r="3" /><circle className="twilabeth-dot" cx="-17" cy="-26" r="3" />
        </g>

        <g className="twilabeth-node twilabeth-node-api" transform="translate(780 460)">
          <circle r="82" className="twilabeth-node-halo" />
          <circle r="68" className="twilabeth-node-surface" />
          <path className="twilabeth-icon-stroke" d="M-42-32h-10c-8 0-12 4-12 12v12c0 8-5 12-12 12 7 0 12 4 12 12v12c0 8 4 12 12 12h10M42-32h10c8 0 12 4 12 12v12c0 8 5 12 12 12-7 0-12 4-12 12v12c0 8-4 12-12 12H42" />
          <path className="twilabeth-accent-stroke" d="M-25 4h19M6 4h19M-6-12v32M6-12v32" />
        </g>

        <g className="twilabeth-node twilabeth-node-code" transform="translate(500 535)">
          <circle r="72" className="twilabeth-node-halo" />
          <circle r="59" className="twilabeth-node-surface" />
          <rect className="twilabeth-icon-stroke" x="-39" y="-31" width="78" height="62" rx="7" />
          <path className="twilabeth-icon-stroke" d="M-39-14h78" />
          <path className="twilabeth-accent-stroke" d="M-20 7l11-10M-20 7l11 10M20 7L9-3M20 7L9 17" />
          <path className="twilabeth-icon-stroke" d="M5-6L-5 20" />
        </g>

        <g className="twilabeth-core" transform="translate(500 310)">
          <circle r="125" className="twilabeth-core-orbit twilabeth-core-orbit-outer" />
          <circle r="108" className="twilabeth-core-orbit twilabeth-core-orbit-inner" />
          <circle r="88" className="twilabeth-core-surface" />
          <path className="twilabeth-icon-stroke twilabeth-core-message" d="M-47-36h68c14 0 25 11 25 25v30c0 14-11 25-25 25h-27l-31 27 7-27h-17c-14 0-25-11-25-25v-30c0-14 11-25 25-25z" />
          <circle className="twilabeth-core-dot" cx="-27" cy="6" r="7" /><circle className="twilabeth-core-dot" cx="-4" cy="6" r="7" /><circle className="twilabeth-core-dot" cx="19" cy="6" r="7" />
          <circle className="twilabeth-orbit-marker" cx="0" cy="-125" r="7" />
        </g>
      </svg>
    </div>
  )
}

export function ProjectGraphic({ src, alt, className = '', loading = 'lazy', animated = false }: ProjectGraphicProps) {
  if (animated) return <TwilabethGraphic alt={alt} className={className} />

  const usesAvif = src.endsWith('.avif')
  const fallbackSrc = usesAvif ? src.replace(/\.avif$/, '.jpg') : src

  return (
    <picture className={`project-graphic ${className}`.trim()}>
      {usesAvif ? <source srcSet={src} type="image/avif" /> : null}
      <img
        src={fallbackSrc}
        alt={alt}
        width="1672"
        height="941"
        loading={loading}
        decoding="async"
      />
    </picture>
  )
}
